import { AppSportsBookState } from 'src/domains/sportsbook/state/AppSportsBook.state';
import * as React from 'react';

import {
    BackButton,
    EventNavigation,
} from 'src/domains/layouts/webview/components/eventNavigation/CarouselSelection.style';
import { CarouselEventNavigation } from 'src/domains/layouts/webview/components/eventNavigation/CarouselEventNavigation';
import { EventsTableMobX } from 'src/domains/sportsbook/shared/Components';
import { EventsTableUnified } from 'src/domains/sportsbook/webview/components/sportEventsListNew/EventsTableUnified';
import {
    getHeaderOptions,
    getDefaultHeaderOptions,
} from 'src/domains/layouts/webview/components/sports/general/config';
import { LayoutSkeleton } from 'src/domains/layouts/webview/modules/LayoutSkeleton';
import { observer } from 'src/utils/mobx-react';
import { SportEventsList } from 'src/domains/layouts/webview/components/sportEventsList/SportEventsList';
import { SportHeaderStar } from 'src/domains/layouts/webview/components/sportHeader/SportHeader';
import { StarRouter } from 'src/domains/layouts/state/router/StarRouter';
import { SectionWrapper, Wrapper } from './SportPage.style';

import { SportRouteType } from 'src/domains/layouts/state/router/newRouter/mainRouteTypes';
import { RightHandSideViewType } from 'src/domains/layouts/state/router/newRouter/rhsRouteType';
import { SeoPagesContent } from 'src/domains/layouts/webview/components/seoPages/SeoPages';
import { useCommon } from 'src/domains/common/Common';
import { NewSportRacingContent } from 'src/domains/sportsbook/webview/components/racing/NewSportRacingContent';
import { ConfigComponents } from 'src/domains/layouts/config/features/config';
import { SportHeaderSimple } from 'src/domains/layouts/webview/components/sportHeader/SportHeaderSimple';

interface SportPageGeneralPropsType {
    sportId: string;
    isAmericanFootball: boolean;
}

const specialCompetitionSlug = 'premier-league';

const SportPageGeneral = observer('SportPageGeneral', (props: SportPageGeneralPropsType): JSX.Element | null => {
    const { sportId, isAmericanFootball } = props;
    const optionItem = getHeaderOptions();
    const options = optionItem[sportId] ?? getDefaultHeaderOptions();

    const common = useCommon();
    const { unifiedSportEventsTable } = ConfigComponents.get(common).unifiedComponentConfig;

    if (unifiedSportEventsTable) {
        return (
            <section className='sport-general'>
                <SportHeaderSimple sportId={sportId} />
                <EventsTableUnified
                    competitionId={null}
                    sportId={sportId}
                />
            </section>
        );
    }

    if (sportId === 'football') {
        return (
            <section className='sport-general'>
                <SportHeaderStar
                    options={options}
                    sportId={sportId}
                    isCompetitionPage={false}
                    competitionId={null}
                />
                <EventsTableMobX
                    competitionId={null}
                    sportId={sportId}
                />
            </section>
        );
    }

    return (
        <section className='sport-general'>
            <SportHeaderStar
                options={options}
                sportId={sportId}
                isCompetitionPage={false}
                competitionId={null}
            />
            <SportEventsList
                key='sport-events-list'
                sport={sportId}
                isAmericanFootball={isAmericanFootball}
            />
        </section>
    );
});

const renderSportPageNavigation = (starRouter: StarRouter, currentView: SportRouteType): React.ReactNode => {
    const hideCompetitions = currentView.id === specialCompetitionSlug;

    if (hideCompetitions) {
        return null;
    }

    return (
        <EventNavigation>
            <BackButton
                onClick={starRouter.redirectToHomepage}
                position='left'
            />

            <CarouselEventNavigation currentView={currentView} />
        </EventNavigation>
    );
};

interface SportPagePropsType {
    currentView: SportRouteType;
    accountView: RightHandSideViewType | null;
    isSearchActive: boolean;
    isAmericanFootball: boolean;
}

export const SportPage = observer('SportPage', (props: SportPagePropsType) => {
    const common = useCommon();
    const appSportsBookState = AppSportsBookState.get(common);
    const { currentView, accountView, isSearchActive, isAmericanFootball } = props;
    const starRouter = StarRouter.get(common);
    const {
        unifiedSportEventsTable,
        componentVariants: { sportsbookVariant },
    } = ConfigComponents.get(common).unifiedComponentConfig;

    if (currentView.nameType === 'races') {
        return (
            <SectionWrapper>
                <LayoutSkeleton
                    currentView={currentView}
                    accountView={accountView}
                    isSearchActive={isSearchActive}
                    navigationType='global'
                >
                    <Wrapper>
                        <NewSportRacingContent
                            key={currentView.id}
                            sport={currentView.id}
                            eventId={appSportsBookState.models.id.getEventIdOption(currentView.event?.id ?? null)}
                            filterType={currentView.type}
                        />
                    </Wrapper>
                </LayoutSkeleton>
            </SectionWrapper>
        );
    }

    return (
        <SectionWrapper>
            <LayoutSkeleton
                currentView={currentView}
                accountView={accountView}
                isSearchActive={isSearchActive}
                navigationType='global'
            >
                <Wrapper variant={sportsbookVariant}>
                    {unifiedSportEventsTable ? null : renderSportPageNavigation(starRouter, currentView)}
                    <SportPageGeneral
                        sportId={currentView.id}
                        isAmericanFootball={isAmericanFootball}
                    />
                    <SeoPagesContent pageSlug={currentView.id} />
                </Wrapper>
            </LayoutSkeleton>
        </SectionWrapper>
    );
});
